import BigNumber from 'bignumber.js';
import React from 'react';
import { AppError, ErrorCodes } from '@ywwwtseng/ywjs';
import * as web3 from '@ywwwtseng/web3';
import { TOKEN_ID, MOONINI_TOKEN_ID, NETWORKS, UNIFIED_TOKENS, LOG_TYPE } from '@/constants';
import type { SelectToken, SelectUserBalance } from '@/types';

export function getTokenActivityType(type: string) {
  if (type === LOG_TYPE.AD_REWARD) {
    return 'log_type.ad_reward';
  }

  if (type === LOG_TYPE.GIFT_CREATED) {
    return 'log_type.gift';
  }

  if (type === LOG_TYPE.WITHDRAW) {
    return 'log_type.withdraw';
  }

  if (type === LOG_TYPE.WITHDRAW_FEE) {
    return 'log_type.withdraw_fee';
  }

  if (type === LOG_TYPE.GIFT_REFUNDED) {
    return 'log_type.gift_refunded';
  }

  if (type === LOG_TYPE.CHAT_FEE) {
    return 'log_type.chat_fee';
  }

  if (type === LOG_TYPE.DEPOSIT) {
    return 'log_type.deposit';
  }

  if (type === LOG_TYPE.WITHDRAW_FEE_REFUND) {
    return 'log_type.withdraw_fee_refund';
  }
  if (type === LOG_TYPE.WITHDRAW_REFUND) {
    return 'log_type.withdraw_refund';
  }

  if (type === LOG_TYPE.SHARE_GIFT_SYSTEM_WITHDRAW) {
    return 'log_type.share_gift_system_withdraw';
  }

  if (type.startsWith(LOG_TYPE.MISSION_REDEEMED_JOIN_CHANNEL())) {
    return 'log_type.mission_redeemed_join_channel';
  }
  if (type.startsWith(LOG_TYPE.MISSION_REDEEMED_JOIN_GROUP())) {
    return 'log_type.mission_redeemed_join_group';
  }
  if (type.startsWith(LOG_TYPE.MISSION_REDEEMED_FOLLOW_X())) {
    return 'log_type.mission_redeemed_follow_x';
  }
  if (type.startsWith(LOG_TYPE.MISSION_REDEEMED_INVITE())) {
    return 'log_type.mission_redeemed_invite';
  }
  if (type.startsWith(LOG_TYPE.GIFT_CLAIMED())) {
    return 'log_type.gift_claimed';
  }

  if (type.startsWith(LOG_TYPE.MOONINI_AIRDROP())) {
    return 'log_type.moonini_airdrop';
  }

  if (type.startsWith(LOG_TYPE.SHARE_GIFT_INVITED())) {
    return 'log_type.share_gift_invited';
  }

  if (type.startsWith(LOG_TYPE.SHARE_GIFT_CLAIMED())) {
    return 'log_type.share_gift_claimed';
  }

  if (type.startsWith(LOG_TYPE.GIFT_BONUS())) {
    return 'log_type.gift_bonus';
  }

  return type;
}

export function getNativeTokenId(src: string) {
  switch (src) {
    case NETWORKS.SOLANA:
    case web3.NATIVE_TOKEN_POOL_PAIRS.SOLANA:
      return TOKEN_ID.SOL;
    case NETWORKS.BSC:
    case web3.NATIVE_TOKEN_POOL_PAIRS.BSC:
      return TOKEN_ID.BNB;
    case NETWORKS.ETHEREUM:
    case web3.NATIVE_TOKEN_POOL_PAIRS.ETH:
      return TOKEN_ID.ETH;
    case NETWORKS.TON:
    case web3.NATIVE_TOKEN_POOL_PAIRS.TON:
      return TOKEN_ID.TON;
    case NETWORKS.TRON:
    case web3.NATIVE_TOKEN_POOL_PAIRS.TRON:
      return TOKEN_ID.TRX;
    case NETWORKS.BTC:
    case web3.NATIVE_TOKEN_POOL_PAIRS.BTC:
      return TOKEN_ID.BTC;
    default:
      throw new AppError(ErrorCodes.INVALID_PARAMS, 'Invalid source', {
        src,
      });
  }
}

export function getPrice(
  prices: { symbol: string; price: string }[] | undefined,
  tokenId: string
) {
  if (getTokenSymbol(tokenId) === 'USDT') {
    return '1';
  }

  return prices?.find((price) => getNativeTokenId(price.symbol) === tokenId)
    ?.price;
}

export function getBalanceTotal(tokens: SelectToken[], balances: SelectUserBalance[]) {
  const value = balances?.reduce((acc, balance) => {
    if (!balance.token_id) {
      return acc;
    }


    const token = tokens?.find((token) => token.id === balance.token_id);

    let usd_price = token?.usd_price;

    if (!usd_price && balance.token_id === 'USDT') {
      usd_price = '1';
    }


    if (!usd_price) {
      return acc;
    }

    return (
      acc +
      Number(usd_price) *
      Number(web3.utils.formatUnits(balance.amount, token?.decimals ?? 0))
    );
  }, 0);

  const formatted = displayAmount(value ?? 0);

  return {
    value,
    formatted,
  };
}

export function formatTinyAmount(
  value: string | number,
  significantDigits = 2
) {
  const str = typeof value === 'number'
    ? new BigNumber(value).toFixed()
    : value

  if (!str.startsWith('0.')) return str

  const decimals = str.slice(2)

  // 計算連續 0
  const zeroMatch = decimals.match(/^0+/)
  if (!zeroMatch) return str

  const zeroCount = zeroMatch[0].length
  const rest = decimals.slice(zeroCount)

  if (!rest) return str

  // 获取有效数字，并去掉末尾的 0
  let significant = rest.slice(0, significantDigits)
  // 去掉末尾的 0
  significant = significant.replace(/0+$/, '')

  return {
    zeroCount,
    significant,
  }
}

export function displayAmountWithDecimals(
  v: string | number | undefined | null,
  decimals: number
) {
  if (v === undefined || v === null) {
    return undefined;
  }
  return displayAmount(web3.utils.formatUnits(String(v), decimals), { showTinyAmount: false });
}

// Function overloads for different return types based on showTinyAmount
export function displayAmount(
  v: string | number,
  options: { showTinyAmount: false }
): string;
export function displayAmount(
  v: string | number,
  options?: { showTinyAmount?: true }
): string | React.ReactElement;
export function displayAmount(
  v: string | number | undefined | null,
  options?: { showTinyAmount?: boolean }
): string | React.ReactElement | undefined;
export function displayAmount(
  v: string | number | undefined | null,
  { showTinyAmount = true }: { showTinyAmount?: boolean } = { showTinyAmount: true }
): string | React.ReactElement | undefined {
  if (v === undefined || v === null) {
    return undefined;
  }

  if (v === '0' || v === 0) {
    return '0';
  }

  let digits = 0;
  const number = new BigNumber(v);

  if (number.gt(10000)) {
    digits = 1;
  } else if (number.gt(1000)) {
    digits = 2;
  } else if (number.gt(100)) {
    digits = 2;
  } else if (number.gt(10)) {
    digits = 2;
  } else if (number.eq(1)) {
    digits = 2;
  } else if (number.gt(1)) {
    digits = 3;
  } else if (number.eq(0)) {
    digits = 2;
  } else if (number.gt(0.1)) {
    digits = 3;
  } else if (number.gt(0.01)) {
    digits = 4;
  } else {
    digits = 8;
  }

  if (showTinyAmount && number.lt(1e-5)) {
    const result = formatTinyAmount(v);

    if (typeof result === 'object') {
      const { zeroCount, significant } = result;

      return (
        <span>
          0.0
          <sub>{zeroCount} </sub>
          {significant}
        </span>
      )

    }
  }

  // 無條件捨去（向下取整）
  // const multiplier = Math.pow(10, digits);
  // const floored = number.multipliedBy(multiplier).integerValue(BigNumber.ROUND_DOWN).dividedBy(multiplier);

  return number.toFixed(digits).replace(/\.?0+$/, '');
}

export function getBalancePrice({
  token: { price, decimals },
  amount,
}: {
  token: { price: string | undefined; decimals: number };
  amount: string | number;
}) {
  if (!price) {
    return undefined;
  }

  return web3.utils.formatUnits(
    BigNumber(price)
      .multipliedBy(amount)
      .integerValue(BigNumber.ROUND_DOWN)
      .toFixed(),
    decimals
  );
}

export function isInsufficient({
  balance,
  amount,
  decimals,
}: {
  balance: string;
  amount: string;
  decimals?: number;
}) {
  return new BigNumber(balance).lt(
    web3.utils.parseUnits(amount, decimals ?? 0)
  );
}

export function parseTokenId(token_id: string): {
  symbol: string;
  decimals: number;
  network: string;
  token_address?: string | null;
} {
  const [symbol, decimals, network, token_address] = token_id.split(':');

  if (!symbol || !decimals) {
    throw new AppError(ErrorCodes.INVALID_PARAMS, 'Invalid token id', {
      token_id,
    });
  }

  return { symbol, decimals: Number(decimals), network, token_address };
}

export function pair(token_id?: string | null) {
  if (!token_id) {
    return null;
  }

  return `${getTokenSymbol(token_id)}USDT`;
}

export function calculateAmountForUsd(
  tokenPrice: string | number | undefined | null,
  targetUsd = 0.01
) {
  if (tokenPrice === undefined || tokenPrice === null) return undefined;

  const price = new BigNumber(tokenPrice);
  if (price.lte(0)) return '0';

  const result = new BigNumber(targetUsd).dividedBy(price);

  // >= 1 → 無條件取整
  if (result.gte(1)) {
    return result.integerValue(BigNumber.ROUND_FLOOR).toFixed();
  }

  // < 1 → 取第一個有效位數（例如 0.0001222 → 0.0001）
  return result.toPrecision(1);
}

export function getTokenSymbol(token: SelectToken | string | undefined | null) {
  if (!token) {
    return undefined;
  }

  if (typeof token === 'string') {
    if (token.includes(':')) {
      const { symbol, network } = parseTokenId(token);

      if (network === NETWORKS.TON && symbol === 'USD₮') {
        return 'USDT';
      }

      return symbol;
    }

    for (const unified of UNIFIED_TOKENS) {
      if (unified.id === token) {
        return unified.symbol;
      }
    }

    throw new AppError(ErrorCodes.INVALID_PARAMS, 'Invalid token id', {
      token,
    });
  }

  for (const unified of UNIFIED_TOKENS) {
    if (
      unified.tokens.some(
        ({ token_address, network }) =>
          token_address === token.token_address && network === token.network
      )
    ) {
      return unified.symbol;
    }
  }

  return token.symbol;
}

export function getTokenPrice(tokens: SelectToken[], tokenId: string): string | undefined {
  if (!tokenId) {
    return undefined;
  }

  if (tokenId === 'USDT') {
    return '1';
  }

  const token = tokens?.find((token) => token.id === tokenId);

  if (!token) {
    return undefined;
  }

  return token.usd_price ?? undefined;
}

export function getTokenIcon(token_id: string) {
  if (token_id === TOKEN_ID.BNB) {
    return 'https://app.moonpacket.com/images/tokens/bnb.png';
  }
  if (token_id === TOKEN_ID.SOL) {
    return 'https://app.moonpacket.com/images/tokens/solana.png';
  }
  if (token_id === TOKEN_ID.ETH) {
    return 'https://app.moonpacket.com/images/tokens/eth.png';
  }
  if (token_id === TOKEN_ID.TON) {
    return 'https://app.moonpacket.com/images/tokens/ton.png';
  }
  if (token_id === TOKEN_ID.TRX) {
    return 'https://app.moonpacket.com/images/tokens/trx.png';
  }
  if (token_id === TOKEN_ID.BTC) {
    return 'https://app.moonpacket.com/images/tokens/btc.png';
  }

  if (token_id === MOONINI_TOKEN_ID) {
    return 'https://app.moonpacket.com/images/tokens/moonini.png';
  }

  const { token_address, network } = parseTokenId(token_id);

  for (const unified of UNIFIED_TOKENS) {
    if (
      unified.tokens.some(
        (token) =>
          token.token_address === token_address && token.network === network
      )
    ) {
      return unified.icon;
    }
  }

  return `https://${process.env.BUCKET_DOMAIN_NAME}/tokens/${network.toLowerCase()}/${token_address}`;
}
